import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addToCart, removeFromCart } from "../slices/cartSlice";
import {
  Container,
  Row,
  Col,
  Image,
  Button,
  Card,
  Badge,
} from "react-bootstrap";
import {
  FaTrash,
  FaRupeeSign,
  FaShoppingCart,
  FaArrowLeft,
  FaPlus,
} from "react-icons/fa";

const CartScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart) || {};

  const {
    cartItems = [],
    itemsPrice = 0,
    totalDiscount = 0,
    deliveryCharge = 0,
    shippingPrice = 0, // same as deliveryCharge
    totalPrice = 0,
  } = cart;

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
  };

  const checkoutHandler = () => {
    navigate("/shipping");
  };

  return (
    <Container className="my-4">
      <h2 className="mb-4 text-primary">
        <FaShoppingCart className="me-2" />
        Your Shopping Cart
      </h2>

      {cartItems.length === 0 ? (
        <div className="text-center my-5">
          <h5 className="text-muted">Your cart is currently empty.</h5>
          <Button variant="outline-primary" onClick={() => navigate("/")}>
            <FaArrowLeft className="me-2" />
            Go Back to Shop
          </Button>
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <Row>
            {cartItems.map((item) => {
              const price = item.price ?? 0;
              const discount = item.discount ?? 0;
              const qty = item.qty ?? 0;

              const subTotal = (price - discount) * qty;
              const productDiscount = discount * qty;

              return (
                <Col key={item._id} md={12} className="mb-4">
                  <Card className="shadow-sm border-0">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col md={2} sm={12}>
                          <Image
                            src={item.image}
                            alt={item.name}
                            fluid
                            rounded
                            className="border"
                          />
                        </Col>

                        <Col md={3} sm={12}>
                          <h5
                            className="text-decoration-underline text-primary"
                            role="button"
                            onClick={() =>
                              navigate(`/product/${item._id}`)
                            }
                          >
                            {item.name}
                          </h5>

                          <div className="mt-2">
                            <Badge bg="info" className="me-2">
                              Price: <FaRupeeSign /> {price.toFixed(2)}
                            </Badge>
                            <Badge bg="warning" text="dark">
                              Qty: {qty}
                            </Badge>
                          </div>
                        </Col>

                        <Col md={4} sm={12}>
                          <Badge bg="success" className="mb-2">
                            Subtotal: <FaRupeeSign /> {subTotal.toFixed(2)}
                          </Badge>
                          <div className="text-muted small">
                            Product Discount:{" "}
                            <span className="text-success">
                              <FaRupeeSign /> {productDiscount.toFixed(2)}
                            </span>
                          </div>
                        </Col>

                        <Col md={3} className="text-end">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeFromCartHandler(item._id)}
                          >
                            <FaTrash className="me-1" />
                            Remove
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* Summary Section */}
          <Card className="mt-4 bg-light shadow-sm border-0">
            <Card.Body>
              <Row>
                <Col md={8}>
                  <h5>
                    🧾 Summary ({cartItems.length} item
                    {cartItems.length > 1 ? "s" : ""})
                  </h5>

                  <p>
                    <strong>Total Items:</strong>{" "}
                    {cartItems.reduce(
                      (acc, item) => acc + Number(item.qty || 0),
                      0
                    )}
                  </p>

                  <p>
                    <strong>Items Price:</strong>{" "}
                    <span className="text-primary">
                      ₹{itemsPrice.toFixed(2)}
                    </span>
                  </p>

                  <p>
                    <strong>Total Discount:</strong>{" "}
                    <span className="text-success">
                      ₹{totalDiscount.toFixed(2)}
                    </span>
                  </p>

                  <p>
                    <strong>Delivery Charge:</strong>{" "}
                    <span
                      className={
                        deliveryCharge === 0
                          ? "text-success fw-bold"
                          : "text-dark fw-bold"
                      }
                    >
                      {deliveryCharge === 0
                        ? "FREE"
                        : `₹${deliveryCharge.toFixed(2)}`}
                    </span>
                  </p>

                  <p className="fs-5 fw-bold">
                    Final Total: ₹{totalPrice.toFixed(2)}
                  </p>
                </Col>

                <Col
                  md={4}
                  className="d-flex flex-column align-items-end justify-content-center"
                >
                  <Button
                    variant="success"
                    className="mb-2 w-100"
                    onClick={checkoutHandler}
                  >
                    ✅ Proceed to Checkout
                  </Button>

                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate("/")}
                    className="w-100"
                  >
                    <FaPlus className="me-2" />
                    Add More Items
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default CartScreen;
